import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User, Mail, Lock } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import PasswordInput from "@/components/ui/PasswordInput";

const signupSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(8, { message: "Password must be at least 8 characters" }),
  confirmPassword: z.string(),
  businessDetails: z.object({
    businessName: z.string().min(2, { message: "Business name is required" }),
    businessType: z.string().min(1, { message: "Business type is required" }),
    country: z.string().min(1, { message: "Country is required" }),
    county: z.string().min(1, { message: "County is required" }),
  }),
  termsAccepted: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions",
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type SignupFormValues = z.infer<typeof signupSchema>;
type SignupStep = 1 | 2 | 3;

const TempSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<SignupStep>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      businessDetails: {
        businessName: "",
        businessType: "sauna",
        country: "",
        county: "",
      },
      termsAccepted: false,
    },
    mode: "onChange",
  });

  const handleNextStep = () => {
    if (step === 1) {
      form.trigger(["name", "email", "password", "confirmPassword"]).then((isValid) => {
        if (isValid) setStep(2);
      });
    } else if (step === 2) {
      form.trigger(["businessDetails.businessName", "businessDetails.businessType", "businessDetails.country"]).then((isValid) => {
        if (isValid) setStep(3);
      });
    }
  };

  const onSubmit = async (data: SignupFormValues) => {
    if (step < 3) {
      handleNextStep();
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      // Store data in localStorage for demo
      localStorage.setItem("tempSignupData", JSON.stringify(data));
      setIsLoading(false);
      setSuccess(true);
      setTimeout(() => navigate("/sauna-wizard"), 1500);
    }, 1000);
  };

  const goBack = () => {
    if (step > 1) setStep((step - 1) as SignupStep);
  };

  return (
    <>
      <Helmet>
        <title>Temp Signup | SaunaDip</title>
        <meta name="description" content="Temporary signup for demo purposes" />
      </Helmet>
      <div className="min-h-screen bg-sauna-beige py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <Button asChild variant="ghost" className="gap-2 mb-6">
              <Link to="/">
                <ArrowLeft size={16} />
                Back to Home
              </Link>
            </Button>
          </div>
          <div className="flex items-center justify-center px-4">
            <Card className="w-full max-w-md shadow-lg border-none rounded-2xl">
              <CardHeader className="flex flex-col items-center">
                <div className="rounded-full bg-gradient-to-b from-sauna-beige to-sauna-orange p-4 mb-4">
                  <User className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-sauna-navy mb-2">Create Account</CardTitle>
                <CardDescription className="text-sauna-navy/80 mb-4">Join the wellness community</CardDescription>
              </CardHeader>
              <CardContent>
                {success ? (
                  <div className="text-center text-sauna-green font-semibold py-8">Signup successful! Redirecting...</div>
                ) : (
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    {/* Step 1: Basic Info */}
                    {step === 1 && (
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="name" className="text-sauna-navy font-medium">Name</Label>
                          <Input id="name" {...form.register("name")} placeholder="Your name" className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40" />
                          <span className="text-xs text-red-500">{form.formState.errors.name?.message}</span>
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-sauna-navy font-medium">Email</Label>
                          <Input id="email" {...form.register("email")} placeholder="you@example.com" className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40" />
                          <span className="text-xs text-red-500">{form.formState.errors.email?.message}</span>
                        </div>
                        <div>
                          <Label htmlFor="password" className="text-sauna-navy font-medium">Password</Label>
                          <PasswordInput id="password" {...form.register("password")} placeholder="Create a strong password" className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40" />
                          <span className="text-xs text-red-500">{form.formState.errors.password?.message}</span>
                        </div>
                        <div>
                          <Label htmlFor="confirmPassword" className="text-sauna-navy font-medium">Confirm Password</Label>
                          <PasswordInput id="confirmPassword" {...form.register("confirmPassword")} placeholder="Confirm your password" className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40" />
                          <span className="text-xs text-red-500">{form.formState.errors.confirmPassword?.message}</span>
                        </div>
                      </div>
                    )}
                    {/* Step 2: Business Info */}
                    {step === 2 && (
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="businessName" className="text-sauna-navy font-medium">Business Name</Label>
                          <Input id="businessName" {...form.register("businessDetails.businessName")} placeholder="Sauna Paradise" className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40" />
                          <span className="text-xs text-red-500">{form.formState.errors.businessDetails?.businessName?.message}</span>
                        </div>
                        <div>
                          <Label htmlFor="businessType" className="text-sauna-navy font-medium">Business Type</Label>
                          <Input id="businessType" {...form.register("businessDetails.businessType")} placeholder="sauna" className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40" />
                          <span className="text-xs text-red-500">{form.formState.errors.businessDetails?.businessType?.message}</span>
                        </div>
                        <div>
                          <Label htmlFor="country" className="text-sauna-navy font-medium">Country</Label>
                          <Input id="country" {...form.register("businessDetails.country")} placeholder="Country" className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40" />
                          <span className="text-xs text-red-500">{form.formState.errors.businessDetails?.country?.message}</span>
                        </div>
                        <div>
                          <Label htmlFor="county" className="text-sauna-navy font-medium">County</Label>
                          <Input id="county" {...form.register("businessDetails.county")} placeholder="County" className="bg-white border border-sauna-beige focus:border-sauna-orange focus:ring-2 focus:ring-sauna-orange/20 placeholder-sauna-navy/40" />
                          <span className="text-xs text-red-500">{form.formState.errors.businessDetails?.county?.message}</span>
                        </div>
                      </div>
                    )}
                    {/* Step 3: Terms */}
                    {step === 3 && (
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="termsAccepted" className="flex items-center gap-2 text-sauna-navy font-medium">
                            <input type="checkbox" id="termsAccepted" {...form.register("termsAccepted")} className="accent-sauna-orange" />
                            I accept the <Link to="/terms-and-conditions" className="text-sauna-orange underline">terms and conditions</Link>
                          </Label>
                          <span className="text-xs text-red-500">{form.formState.errors.termsAccepted?.message}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between mt-8">
                      <Button type="button" variant="outline" onClick={goBack} disabled={step === 1}>Back</Button>
                      {step < 3 ? (
                        <Button type="button" className="bg-gradient-to-r from-sauna-beige to-sauna-orange text-white" onClick={handleNextStep}>Continue</Button>
                      ) : (
                        <Button type="submit" className="bg-gradient-to-r from-sauna-beige to-sauna-orange text-white" disabled={isLoading}>{isLoading ? 'Signing up...' : 'Sign Up'}</Button>
                      )}
                    </div>
                  </form>
                )}
                <div className="text-center mt-4 text-sauna-navy/80">
                  Already have an account? <a href="/login" className="text-sauna-orange font-semibold">Sign in</a>
                </div>
                <div className="text-xs text-center text-sauna-navy/60 mt-6">
                  By creating an account, you agree to our <a href="/terms-and-conditions" className="text-sauna-orange underline">Terms of Service</a> and <a href="/privacy-policy" className="text-sauna-orange underline">Privacy Policy</a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default TempSignupPage; 